import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

// AuthContext: provides user object and login/logout methods.
// Responsibilities:
// - Persist token/user to localStorage on login
// - Rehydrate user state on app load
// - Clear auth on logout or session expiration

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
	// Initialize synchronously from localStorage so PrivateRoute sees user on first render
	const [user, setUser] = useState(() => {
		try {
			const userStr = localStorage.getItem('user');
			return userStr ? JSON.parse(userStr) : null;
		} catch (e) {
			return null;
		}
	});

	// Helper: apply token to axios instance
	const applyToken = useCallback((token) => {
		try {
			if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			else delete api.defaults.headers.common['Authorization'];
		} catch (e) {
			// ignore
		}
	}, []);

	// Rehydrate token and user on mount
	useEffect(() => {
		const token = localStorage.getItem('token');
		if (token) applyToken(token);
		const onSessionExpired = () => {
			// Called when API interceptor detects 401/403
			logout(false);
		};
		// Listen for session expiration events dispatched by api interceptor
		window.addEventListener('session:expired', onSessionExpired);

		// Also listen for storage events so multiple tabs stay in sync
		const onStorage = (e) => {
			if (e.key === 'user') {
				try {
					const newUser = e.newValue ? JSON.parse(e.newValue) : null;
					setUser(newUser);
				} catch (err) {
					setUser(null);
				}
			}
			if (e.key === 'token') {
				applyToken(e.newValue);
			}
		};
		window.addEventListener('storage', onStorage);

		return () => {
			window.removeEventListener('session:expired', onSessionExpired);
			window.removeEventListener('storage', onStorage);
		};
	}, [applyToken]);

	// Login: call backend, persist token+user, set axios header and update state
	const login = async (email, password) => {
		const res = await api.post('/auth/login', { email, password });
		const token = res.data.token;
		const userObj = res.data.user;
		if (token) {
			localStorage.setItem('token', token);
			applyToken(token);
		}
		if (userObj) localStorage.setItem('user', JSON.stringify(userObj));
		setUser(userObj || null);

		// Show login success toast
		try { toast.success('Login successful'); } catch (e) { /* ignore */ }
		return userObj;
	};

	// Update local profile: update name/email in localStorage and React state.
	// Note: backend user-update endpoint may not be available; this updates client-only state.
	const updateProfile = async (updates = {}) => {
		try {
			const current = (() => {
				try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
			})();
			const next = { ...(current || user || {}), ...updates };
			localStorage.setItem('user', JSON.stringify(next));
			setUser(next);
			return next;
		} catch (e) {
			return null;
		}
	};

	// Logout: clear local storage, axios header, and React state.
	// If redirectToLanding is true we navigate to '/'.
	const logout = (redirectToLanding = true) => {
		try {
			localStorage.removeItem('token');
			localStorage.removeItem('user');
		} catch (e) {
			// ignore
		}
		try { delete api.defaults.headers.common['Authorization']; } catch (e) {}
		setUser(null);

		if (redirectToLanding) {
			// Use location.assign to force a full reload and reset app state
			window.location.assign('/');
		}
	};

	return (
		<AuthContext.Provider value={{ user, login, logout, updateProfile }}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
